import { SpotlightTray } from '../../../domain/spotlight-tray';
import { CreateMarketingSpotlightCtaPageItemApiModel } from '@cxb/editor-stb-api';
import { mapperPipe, PlatformType, remainderMapper } from 'osn-shared';
import {
  descriptionLocalizedMapperDomainToApi,
  mapLocalizedDomainImagesToCreateLocalizedPlatformImageRequestApiModelArray,
  scheduledDateMapperDomainToApi,
  targetingMapperDomainToApi,
  titleLocalizedFromHeadlineMapper,
} from '../../mapping/create-tray-domain-to-api';
import { MarketingSpotlightCtaPageItemApiModel } from '@cxb/editor-stb-api/model/models';
import {
  commonMapper,
  descriptionLocalizedMapperApiToDomain,
  headlineTextMapper,
  mapApiModelImageWithPlatformToDomain,
  scheduledDateMapperApiToDomain,
} from '../../mapping/create-tray-api-to-domain';
import { SpotlightTrayFormViewModel } from './spotlight-tray-form-view-model';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import {
  mapLocalizedDomainImagesToPlatformLocalizedViewModelImage,
  mapSchedulingDomainToViewModel,
} from '../create-tray-screen/mapping/create-tray-domain-to-vm';
import { mapViewModelImagesToLocalizedDomainImages } from '../create-tray-screen/mapping/create-tray-vm-to-domain';

// 'SpotlightTray'

/**
 * DOMAIN -> API
 */

export const mapSpotlightTrayDomainModelToApiModel = (
  tray: SpotlightTray,
  pageVersionId: string,
  tenantId?: string
): CreateMarketingSpotlightCtaPageItemApiModel => {
  const commonProperties = mapperPipe(
    tray,
    titleLocalizedFromHeadlineMapper,
    targetingMapperDomainToApi,
    descriptionLocalizedMapperDomainToApi,
    scheduledDateMapperDomainToApi
  );

  return remainderMapper(commonProperties, () => ({
    tenantId,
    backgroundImages: mapLocalizedDomainImagesToCreateLocalizedPlatformImageRequestApiModelArray(tray.backgroundImages),
    pageVersionId,
    internalName: tray.titleEnglish,
    buttonTextLocalized: tray.buttonTextLocalized,
    platformIds: tray.platformIds,
  }));
};

/**
 * API -> DOMAIN
 */

export const mapSpotlightTrayApiModelToDomainModel = (tray: MarketingSpotlightCtaPageItemApiModel): SpotlightTray => {
  return remainderMapper(
    mapperPipe(
      tray,
      commonMapper,
      headlineTextMapper,
      descriptionLocalizedMapperApiToDomain,
      // backgroundImagesMapper,
      scheduledDateMapperApiToDomain
    ),
    () => ({
      backgroundImages: {
        en: tray.backgroundImages.en.map((i) => mapApiModelImageWithPlatformToDomain(i)),
        ar: tray.backgroundImages.ar.map((i) => mapApiModelImageWithPlatformToDomain(i)),
      },
      type: 'SpotlightTray',
      titleEnglish: tray.internalName,
      buttonTextLocalized: tray.buttonTextLocalized,
      countryIds: tray.countries.map((country) => country.id),
      userSegmentIds: tray.userSegments.map((userSegment) => userSegment.id),
    })
  );
};

/**
 * DOMAIN -> VIEWMODEL
 */

export const mapSpotlightTrayDomainModelToViewModel = (tray: SpotlightTray): SpotlightTrayFormViewModel => {
  return {
    id: tray.id,
    description: { en: tray.descriptionTextEnglish, ar: tray.descriptionTextArabic },
    headlineText: { en: tray.headlineTextEnglish, ar: tray.headlineTextArabic },
    backgroundImages: mapLocalizedDomainImagesToPlatformLocalizedViewModelImage(tray.backgroundImages),
    buttonText: tray.buttonTextLocalized,
    scheduling: mapSchedulingDomainToViewModel(tray),
    moduleName: tray.titleEnglish,
    type: TrayFormViewModelType.SpotlightTray,
    countryIds: tray.countryIds,
    userSegmentIds: tray.userSegmentIds,
  };
};

/**
 * VIEWMODEL -> DOMAIN
 */

export const mapSpotlightTrayViewModelToDomainModel = (
  tray: SpotlightTrayFormViewModel,
  platformIds: Record<PlatformType, string>
): SpotlightTray => {
  return {
    id: tray.id,
    descriptionTextArabic: tray.description.ar,
    descriptionTextEnglish: tray.description.en,
    headlineTextArabic: tray.headlineText.ar,
    headlineTextEnglish: tray.headlineText.en,
    backgroundImages: mapViewModelImagesToLocalizedDomainImages(tray.backgroundImages, platformIds),
    scheduledFrom: tray.scheduling?.scheduledFrom ? new Date(tray.scheduling.scheduledFrom).getTime() : undefined,
    scheduledTo: tray.scheduling?.scheduledTo ? new Date(tray.scheduling.scheduledTo).getTime() : undefined,
    titleEnglish: tray.moduleName,
    countryIds: tray.countryIds,
    userSegmentIds: tray.userSegmentIds,
    buttonTextLocalized: tray.buttonText,
    type: 'SpotlightTray',
    order: undefined,
    visible: undefined,
    draggable: true,
    platformIds: Object.values(platformIds),
  };
};
